import { MESApiService, type spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type {
	CatalogOption,
	ProductSizeOption,
	RFactorPerStandData,
	RFactorPickers,
	RFactorPoint,
} from "../models/RFactorPerStand";

interface RawPointRow {
	Start: string | null;
	StartFixed: string | null;
	RFactorAvg: number | null;
	RFactorMin: number | null;
	RFactorMax: number | null;
}

interface RawHollowRow {
	Start: string | null;
	End: string | null;
	Diff: number | null;
}

interface RawShiftRow {
	TimestampFixed: string | null;
	CrewId: string | null;
}

interface RawMetaRow {
	Index: number;
	Name: string;
}

// The API serialises DATETIME without a zone marker and the procedures return
// UTC, which is what dxtToLocalServerTime assumes when it appends the Z.
const toEpoch = (value: string | null): number | null => {
	if (!value) return null;
	const ms = new Date(`${value}Z`).getTime();
	return Number.isNaN(ms) ? null : ms;
};

// FLOAT(53) cannot hold 1.227 exactly, so a rounded column still arrives as
// 1.2270000000000001 and every comparison downstream inherits the noise.
const round3 = (value: number | null): number | null =>
	value == null ? null : Number.parseFloat(Number(value).toFixed(3));

export interface GetRFactorPerStandParams {
	start: string;
	end: string;
	productSizeId: number;
	standId: number;
	crewId: string | null;
	signal?: AbortSignal;
}

export const getRFactorPerStand = async ({
	start,
	end,
	productSizeId,
	standId,
	crewId,
	signal,
}: GetRFactorPerStandParams): Promise<RFactorPerStandData> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "start", value: start },
		{ name: "end", value: end },
		{ name: "productSizeId", value: productSizeId },
		{ name: "standId", value: standId },
		{ name: "crewId", value: crewId },
	];

	const resp = await apiService.callV2(
		"[MILL].[GetRFactorPerStandDataWeb]",
		parameters,
		signal,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching R-Factor per stand");
	}

	const rawPoints = get(resp, "data.tables[0].rows", []) as RawPointRow[];
	const rawHollows = get(resp, "data.tables[1].rows", []) as RawHollowRow[];
	const rawShifts = get(resp, "data.tables[2].rows", []) as RawShiftRow[];

	const points: RFactorPoint[] = [];
	for (const row of rawPoints) {
		const startMs = toEpoch(row.Start);
		const fixedMs = toEpoch(row.StartFixed);
		// An empty range still returns one row, all columns null: the procedure
		// seeds the series before it knows whether there is anything to seed it with.
		if (startMs == null || fixedMs == null) continue;
		points.push({
			start: startMs,
			startFixed: fixedMs,
			avg: round3(row.RFactorAvg),
			min: round3(row.RFactorMin),
			max: round3(row.RFactorMax),
		});
	}

	// The procedure derives the idle periods from the product's campaigns, which
	// exist whether or not any of their billets carry an R-Factor. A campaign that
	// ran before the first plotted point produces a gap that ends AT that point,
	// so subtracting it shifts the whole series and removes nothing between two
	// points: no marker lands inside the chart. Counting those makes the summary
	// claim more idle time than the whole range holds. Only the gaps that sit
	// between two plotted points are real to the reader.
	const firstStart = points.length ? points[0].start : null;
	const lastStart = points.length ? points[points.length - 1].start : null;

	const hollows = rawHollows.flatMap((row) => {
		const startMs = toEpoch(row.Start);
		const endMs = toEpoch(row.End);
		if (startMs == null || endMs == null) return [];
		if (firstStart == null || lastStart == null) return [];
		if (startMs <= firstStart || startMs >= lastStart) return [];
		return [{ start: startMs, end: endMs, seconds: Number(row.Diff ?? 0) }];
	});

	const shiftChanges = rawShifts.flatMap((row) => {
		const fixedMs = toEpoch(row.TimestampFixed);
		if (fixedMs == null || !row.CrewId) return [];
		return [{ timestampFixed: fixedMs, crew: row.CrewId.trim() }];
	});

	return { points, hollows, shiftChanges };
};

export interface GetRFactorPickersParams {
	start: string;
	end: string;
	signal?: AbortSignal;
}

export const getRFactorPickers = async ({
	start,
	end,
	signal,
}: GetRFactorPickersParams): Promise<RFactorPickers> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "start", value: start },
		{ name: "end", value: end },
	];

	const resp = await apiService.callV2(
		"[MILL].[GetRFactorPerStandPickersDataWeb]",
		parameters,
		signal,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching R-Factor pickers");
	}

	// The first table is an index of the three that follow, by name and position.
	// Reading it rather than assuming the order is how the legacy screen does it.
	const meta = get(resp, "data.tables[0].rows", []) as RawMetaRow[];
	const tableFor = (name: string) => {
		const entry = meta.find((row) => row.Name === name);
		if (!entry) return [];
		return get(resp, `data.tables[${entry.Index}].rows`, []) as unknown[];
	};

	const crews = (tableFor("CREWS") as { Id: string | null }[])
		.map((row) => row.Id?.trim() ?? "")
		.filter(Boolean);

	const productSizes = (tableFor("PRODUCTSIZES") as ProductSizeOption[]).map(
		(row) => ({
			Id: row.Id,
			Name: row.Name?.trim() ?? "",
			ProductTypeId: row.ProductTypeId,
		}),
	);

	const productTypes = (tableFor("PRODUCTTYPES") as CatalogOption[]).map(
		(row) => ({ Id: row.Id, Name: row.Name?.trim() ?? "" }),
	);

	return { crews, productSizes, productTypes };
};
